import { useInputValidation } from '@e2m-customer-portal/hooks';
import { noop, Nullable, Validation } from '@e2m-customer-portal/shared';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select as MuiSelect, SelectProps } from '@mui/material';
import clsx from 'clsx';
import { ComponentType, ReactElement, ReactNode, useId, useState } from 'react';
import { DefaultItemRender } from './DefaultItemRender';
import { DefaultSelectRender } from './DefaultSelectRender';
import { getId, SelectItemType } from './Option';

type OnChangeFunc<T> = (value: T) => void;

interface Classes {
  root?: string;
  inputLabel?: string;
  select?: string;
  input?: string;
}

interface CommonSingleSelectProps<T> {
  /**
   * The text of the label.
   */
  label?: string;

  /**
   * Will get triggered by selecting values with all selected values.
   */
  onChange?: OnChangeFunc<T>;

  /**
   * List of all selectable options.
   */
  options: readonly T[];

  /**
   * The selected value.
   */
  value: Nullable<T>;

  /**
   * Can be used to overwrite how the items in the select dropdown are rendered.
   * @param item
   * @param selected
   */
  ItemComponent?: ComponentType<{ value: T }>;

  /**
   * Can be used to overwrite how the select is rendered.
   * @param option
   */
  SelectComponent?: ComponentType<{ options: readonly T[]; selected: readonly T[] }>;

  /**
   * An optional CSS classes dictionary that will be applied to this element and its children.
   */
  classes?: Classes;
}

interface NonValidatedSingleSelectProps {
  /**
   * The name of the input. Must be set to use validations.
   */
  name?: null;

  /**
   * The rules to validate this input with.
   */
  rules?: null;

  /**
   * Whether this input is required.
   */
  required?: false;
}

interface ValidatedSingleSelectProps<T> {
  /**
   * The name of the input. Must be set to use validations.
   */
  name: string;

  /**
   * The rules to validate this input with.
   */
  rules?: Validation<T>[];

  /**
   * Whether this input is required.
   */
  required: true;
}

export type SingleSelectProps<T> = Omit<
  SelectProps,
  | 'labelId'
  | 'label'
  | 'classes'
  | 'onBlur'
  | 'value'
  | 'open'
  | 'onOpen'
  | 'onClose'
  | 'onChange'
  | 'required'
  | 'error'
> &
  CommonSingleSelectProps<T> &
  (NonValidatedSingleSelectProps | ValidatedSingleSelectProps<T>);

export function SingleSelect<T extends SelectItemType>({
  MenuProps,
  sx,
  className,
  classes: propsClasses,
  color,
  disabled,
  fullWidth,
  label,
  name,
  onChange = noop,
  options,
  ItemComponent = DefaultItemRender,
  SelectComponent = DefaultSelectRender,
  required,
  rules,
  value,
  ...props
}: SingleSelectProps<T>): ReactElement<SingleSelectProps<T>> {
  const [selectOpen, setSelectOpen] = useState(false);
  const { handleBlur, error, message, setValidating } = useInputValidation(name ?? 'single-select', rules, value);
  const labelId = useId();

  const selectedRenderer = (id: unknown): ReactNode => (
    <SelectComponent options={options} selected={options.filter((option) => getId(option) === id)} />
  );

  const handleChange: SelectProps['onChange'] = (event) => {
    const option = options.find((o) => event.target.value === getId(o));
    if (option) {
      onChange(option);
    }
  };

  return (
    <FormControl
      variant="outlined"
      sx={sx}
      color={color}
      className={clsx(propsClasses?.root, className)}
      fullWidth={fullWidth}
      required={required}
      error={error}
    >
      {label && (
        <InputLabel id={labelId} className={propsClasses?.inputLabel}>
          {label}
        </InputLabel>
      )}
      <MuiSelect
        labelId={label && labelId}
        label={label}
        className={propsClasses?.select}
        classes={{ nativeInput: propsClasses?.input }}
        onBlur={handleBlur}
        value={value ? getId(value) : ''}
        renderValue={selectedRenderer}
        open={selectOpen}
        disabled={disabled}
        onOpen={
          disabled
            ? undefined
            : () => {
                setSelectOpen(true);
                setValidating(false);
              }
        }
        onClose={() => {
          setSelectOpen(false);
          setValidating(true);
        }}
        onChange={handleChange}
        fullWidth={fullWidth}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          ...MenuProps,
        }}
        {...props}
      >
        {options.map((option) => (
          <MenuItem key={getId(option)} value={getId(option)}>
            <ItemComponent value={option} />
          </MenuItem>
        ))}
      </MuiSelect>
      {message && <FormHelperText>{message}</FormHelperText>}
    </FormControl>
  );
}
